<template>
  <div>
    <v-data-table
      class="claims-table"
      :server-items-length="totalCount"
      :options.sync="options"
      :loading="loading"
      :headers="headers"
      :items="claims"
      :items-per-page="10"
      @click:row="(i) => expanded.push(i)"
      show-expand
      show-select
      v-model="selected"
      :expanded.sync="expanded"
      item-key="Identifier"
      :footer-props="{ 'items-per-page-options': [5, 10, 20, 50, 100, 500] }"
    >
      <!-- eslint-disable-next-line vue/valid-v-slot -->
      <template v-slot:header.data-table-select="{ props, on }">
        <v-icon
          @click="selected = claims"
          color="white"
          v-if="!props.indeterminate && !props.value"
        >
          mdi-checkbox-blank-outline
        </v-icon>
        <v-simple-checkbox
          color="white"
          v-if="props.indeterminate"
          v-ripple
          v-bind="props"
          :value="props.indeterminate"
          v-on="on"
        ></v-simple-checkbox>
        <v-simple-checkbox
          color="white"
          v-if="props.value"
          v-ripple
          v-bind="props"
          v-on="on"
        ></v-simple-checkbox>
      </template>
      <template v-slot:[`item.Timestamp`]="{ item, index }">
        <td class="text-subtitle-2 text-left" :key="`timestamp-` + index">
          {{ $helpers.formatDate(item.Timestamp) }}
          <!-- {{ item.Timestamp.split(".")[0].replace("T", " ") }} -->
        </td>
      </template>
      <template v-slot:[`item.Patients`]="{ item, index }">
        <td class="text-subtitle-2 text-left" :key="`patients-` + index">
          {{ getPatientNames(item) }}
        </td>
      </template>
      <template v-slot:[`item.Coverage`]="{ item, index }">
        <td class="text-subtitle-2 text-left" :key="`coverages-` + index">
          {{ getCoverageNames(item) }}
        </td>
      </template>
      <template v-slot:[`item.ClaimedAmount`]="{ item, index }">
        <td class="text-subtitle-2 text-left" :key="`claimed-` + index">
          {{ $helpers.toCurrency(calculateClaimedAmount(item)) }}
        </td>
      </template>
      <template v-slot:[`item.PaidTotal`]="{ item, index }">
        <td class="text-subtitle-2 text-left" :key="`paid-` + index">
          {{ $helpers.toCurrency(calculatePaidTotal(item)) }}
        </td>
      </template>
      <template v-slot:[`item.Proof`]="{ item, index }">
        <td class="text-subtitle-2 text-left" :key="`coverages-` + index">
          {{ getProofTypes(item) }}
        </td>
      </template>
      <template v-slot:expanded-item="{ headers, item }">
        <td class="px-0" :colspan="headers.length">
          <claim-detail
            :claim="item"
            @preview-proof="$emit('preview-proof', $event)"
          />
        </td>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import ClaimDetail from "@/components/Claims/ClaimDetail.vue";
import { translate2 } from "@/trans2";
import { mapActions, mapGetters } from "vuex";

export default {
  components: {
    ClaimDetail,
  },
  props: {
    loading: Boolean,
    reset: Boolean,
    tableOptions: {
      type: Object,
      default: () => {},
    },
    claims: Array,
    totalCount: Number,
  },
  data() {
    return {
      options: { sortDesc: [true] },
      page: 1,
      expanded: [],
      selected: [],
      item: null,
    };
  },
  watch: {
    options: {
      handler(v) {
        this.$emit("pagination", v);
      },
      deep: true,
    },
    selected: {
      handler() {
        this.$emit("claims-selected", {
          items: this.selected,
        });
      },
      deep: true,
      immediate: true,
    },
    reset: {
      handler(v) {
        if (v) this.expanded = [];
      },
    },
  },
  computed: {
    ...mapGetters("auth", ["user"]),
    headers() {
      return [
        {
          text: "",
          value: "data-table-select",
          class: `text-subtitle-2 ${
            this.$vuetify.breakpoint.smAndUp ? "primary" : ""
          } white--text select-all-checkbox`,
          cellClass: "single-select",
        },
        {
          text: "",
          value: "data-table-expand",
          cellClass: "expand",
          class: "text-subtitle-2 primary white--text",
        },
        {
          text: this.$t("components.claims_history.header.submission_id"),
          align: "center",
          sortable: true,
          value: "Identifier",
          cellClass: "text-subtitle-2 pl-0",
          class: "text-subtitle-2 primary white--text",
          width: "170px",
        },
        {
          text: this.$t("components.chequerequisitionform.payee"),
          align: "left",
          sortable: false,
          value: "Payee",
          cellClass: "text-subtitle-2",
          class: "text-subtitle-2 primary white--text",
        },
        {
          text: this.$t("components.claims_history.header.patient"),
          align: "left",
          sortable: false,
          value: "Patients",
          cellClass: "text-subtitle-2",
          class: "text-subtitle-2 primary white--text",
        },
        {
          text: this.$t("globals.coverage"),
          align: "left",
          sortable: false,
          value: "Coverage",
          cellClass: "text-subtitle-2",
          class: "text-subtitle-2 primary white--text",
        },
        {
          text: this.$t("components.claims_history.header.submission_date"),
          align: "left",
          sortable: true,
          value: "Timestamp",
          cellClass: "text-subtitle-2",
          class: "text-subtitle-2 primary white--text",
          width: "180px",
        },
        {
          text: this.$t("components.claims_history.header.claimed_total"),
          align: "left",
          sortable: false,
          value: "ClaimedAmount",
          cellClass: "text-subtitle-2",
          class: "text-subtitle-2 primary white--text",
        },
        {
          text: this.$t("components.claims_history.header.total_paid"),
          align: "left",
          sortable: false,
          value: "PaidTotal",
          cellClass: "text-subtitle-2",
          class: "text-subtitle-2 primary white--text",
        },
        {
          text: this.$t("components.chequerequisitionform.payee"),
          align: "left",
          sortable: false,
          value: "Payee",
          cellClass: "text-subtitle-2",
          class: "text-subtitle-2 primary white--text",
        },
        {
          text: this.$t("components.claims_history.header.proof"),
          align: "left",
          sortable: false,
          value: "Proof",
          cellClass: "text-subtitle-2",
          class: "text-subtitle-2 primary white--text",
        },
      ];
    },
  },
  methods: {
    translate2(item, field) {
      return translate2(item, field);
    },
    getPatientNames(claim) {
      const patients = claim.Claims.map((c) => {
        return c.FirstName + " " + c.LastName;
      });
      return [...new Set(patients)].join(", ");
    },

    getCoverageNames(claim) {
      const coverages = claim.Claims.map((c) => {
        // return translate2(c.CurrentClaimData.PlanBenefit.Benefit, "Name");
        return c.CurrentClaimData.PlanBenefit.Benefit.Name;
      });
      return [...new Set(coverages)].join(", ");
    },

    getProofTypes(claim) {
      // const proofs = [];
      // for (let c of claim.Claims) {
      //   for (let proof of c.CurrentClaimData.Proofs) {
      //     proofs.push()
      //   }
      // }
      const proofs = claim.Claims.map((o) =>
        o.Proofs.map((c) => c.ProofType)
      );
      return [...new Set(proofs.flat(1))].join(", ");
    },
    calculateClaimedAmount(claim) {
      let claimsTotal = 0;
      let claimsCob = 0;
      let totalFee = 0;
      for (let c of claim.Claims) {
          claimsCob = claimsCob + c.CurrentClaimData.COBAmount;
          claimsTotal = claimsTotal + c.CurrentClaimData.ExpenseAmount;
          let labFee = parseFloat(c.CurrentClaimData.LabFee);
          if (isNaN(labFee)) {
            labFee = 0.0;
          }
          totalFee = totalFee + labFee;
      }
      return claimsTotal + totalFee - claimsCob;
    },
    calculatePaidTotal(claim) {
      let total = 0;
      for (let c of claim.Claims) {
        let paid = parseFloat(c.CurrentClaimData.PaidAmount);
        if (isNaN(paid)) {
          paid = 0.0;
        }
        let paidLabFee = parseFloat(c.CurrentClaimData.PaidLabFee);
        if (isNaN(paidLabFee)) {
          paidLabFee = 0.0;
        }
        total = total + paid + paidLabFee;
      }
      return total;
    },
  },
};
</script>
<style scoped>
.claims-table >>> tbody tr:hover {
  cursor: pointer;
}
.claims-table >>> .v-data-table-header__icon {
  color: white !important;
}
.claims-table >>> th {
  padding: 0 8px !important;
}
.claims-table >>> td {
  padding: 0 8px !important;
}
.claims-table >>> th.select-all-checkbox {
  padding-left: 8px !important;
}
.claims-table >>> td.single-select {
  padding-right: 4px !important;
}
.claims-table >>> td.expand {
  padding-right: 14px !important;
}
.claims-table >>> .v-data-table__expanded__row {
  background-color: #cbcbcb !important;
}
.select-all-checkbox >>> .theme--light.v-icon {
  color: white !important;
}
.claims-table >>> .expand {
  padding: 0;
}
.v-tooltip__content {
  max-width: 100px !important;
}
</style>
